import { supabase } from '../lib/supabase';

/**
 * Service for managing micro lessons (bite-sized sales training content)
 */

/**
 * Fetch all micro lessons with optional filters
 */
export const fetchMicroLessons = async (filters = {}) => {
  try {
    let query = supabase?.from('micro_lessons')?.select('*')?.order('created_at', { ascending: false });

    if (filters?.tenantId) {
      query = query?.eq('tenant_id', filters.tenantId);
    }

    // Apply industry filter
    if (filters?.industry && filters?.industry !== 'all') {
      query = query?.eq('industry', filters?.industry);
    }

    // Apply difficulty filter
    if (filters?.difficulty && filters?.difficulty !== 'all') {
      query = query?.eq('difficulty', parseInt(filters?.difficulty));
    }

    // Apply search filter
    if (filters?.search && filters?.search?.trim()) {
      query = query?.or(
        `title.ilike.%${filters?.search}%,lesson_text.ilike.%${filters?.search}%,tags.ilike.%${filters?.search}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(lesson => ({
      id: lesson?.id,
      industry: lesson?.industry,
      title: lesson?.title,
      lessonText: lesson?.lesson_text,
      keySteps: lesson?.key_steps,
      exampleDialogue: lesson?.example_dialogue,
      tags: lesson?.tags,
      difficulty: lesson?.difficulty,
      createdAt: lesson?.created_at
    }));
  } catch (error) {
    console.error('Error fetching micro lessons:', error);
    throw error;
  }
};

/**
 * Fetch a single micro lesson by ID
 */
export const fetchMicroLessonById = async (id, tenantId = null) => {
  try {
    const { data, error } = await supabase
      ?.from('micro_lessons')
      ?.select('*')
      ?.eq('id', id)
      ?.eq(tenantId ? 'tenant_id' : 'id', tenantId || id)
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      industry: data?.industry,
      title: data?.title,
      lessonText: data?.lesson_text,
      keySteps: data?.key_steps,
      exampleDialogue: data?.example_dialogue,
      tags: data?.tags,
      difficulty: data?.difficulty,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error fetching micro lesson:', error);
    throw error;
  }
};

/**
 * Create a new micro lesson (admin only)
 */
export const createMicroLesson = async (lessonData, tenantId = null) => {
  try {
    // Convert camelCase to snake_case
    const { data, error } = await supabase?.from('micro_lessons')?.insert([{
        industry: lessonData?.industry,
        title: lessonData?.title,
        lesson_text: lessonData?.lessonText,
        key_steps: lessonData?.keySteps,
        example_dialogue: lessonData?.exampleDialogue || null,
        tags: lessonData?.tags || null,
        difficulty: lessonData?.difficulty || 1,
        tenant_id: tenantId || null
      }])?.select()?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      industry: data?.industry,
      title: data?.title,
      lessonText: data?.lesson_text,
      keySteps: data?.key_steps,
      exampleDialogue: data?.example_dialogue,
      tags: data?.tags,
      difficulty: data?.difficulty,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error creating micro lesson:', error);
    throw error;
  }
};

/**
 * Update an existing micro lesson (admin only)
 */
export const updateMicroLesson = async (id, lessonData, tenantId = null) => {
  try {
    // Convert camelCase to snake_case
    const updateData = {};
    if (lessonData?.industry !== undefined) updateData.industry = lessonData?.industry;
    if (lessonData?.title !== undefined) updateData.title = lessonData?.title;
    if (lessonData?.lessonText !== undefined) updateData.lesson_text = lessonData?.lessonText;
    if (lessonData?.keySteps !== undefined) updateData.key_steps = lessonData?.keySteps;
    if (lessonData?.exampleDialogue !== undefined) updateData.example_dialogue = lessonData?.exampleDialogue;
    if (lessonData?.tags !== undefined) updateData.tags = lessonData?.tags;
    if (lessonData?.difficulty !== undefined) updateData.difficulty = lessonData?.difficulty;

    if (tenantId) updateData.tenant_id = tenantId;

    const { data, error } = await supabase
      ?.from('micro_lessons')
      ?.update(updateData)
      ?.eq('id', id)
      ?.eq(tenantId ? 'tenant_id' : 'id', tenantId || id)
      ?.select()
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      industry: data?.industry,
      title: data?.title,
      lessonText: data?.lesson_text,
      keySteps: data?.key_steps,
      exampleDialogue: data?.example_dialogue,
      tags: data?.tags,
      difficulty: data?.difficulty,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error updating micro lesson:', error);
    throw error;
  }
};

/**
 * Delete a micro lesson (admin only)
 */
export const deleteMicroLesson = async (id, tenantId = null) => {
  try {
    let query = supabase?.from('micro_lessons')?.delete()?.eq('id', id);
    if (tenantId) {
      query = query?.eq('tenant_id', tenantId);
    }

    const { error } = await query;

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error deleting micro lesson:', error);
    throw error;
  }
};

/**
 * Get unique industries from micro lessons
 */
export const fetchMicroLessonIndustries = async (tenantId = null) => {
  try {
    let query = supabase?.from('micro_lessons')?.select('industry')?.order('industry');
    if (tenantId) {
      query = query?.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Get unique industries
    const industries = [...new Set(data.map(item => item.industry))];
    return industries;
  } catch (error) {
    console.error('Error fetching micro lesson industries:', error);
    throw error;
  }
};